import React, { useCallback, useState } from 'react';
import { View, Text, Modal, ScrollView, TouchableOpacity } from 'react-native';
import { useTranslation } from '@oolio-group/localization';
import { v4 as uuidv4 } from 'uuid';
import {
  KdsModifierStyle,
  ModifierKeywordMatchMode,
} from '@oolio-group/domain';
import SetModifierStyleModalStyles from './SetModifierStyleModal.styles';
import {
  validateModifierStyle,
  MAX_NAME_LENGTH,
  MAX_CONDITIONS_PER_STYLE,
} from '../../../utils/validateModifierStyle';
import InputText from '../../Input/InputText';
import Button from '../../Buttons/ButtonText';
import Icon from '../../Icon/Icon';
import theme from '../../../common/theme';
import { useColors } from '../../../hooks/useColors';
import {
  modifierTextColorStyle,
  modifierBackgroundColorStyle,
} from '../../../common/modifierColorStyles';

export enum ModifierStyleAction {
  CREATE = 'create',
  EDIT = 'edit',
}

export interface SetModifierStyleModalProps {
  visible: boolean;
  action: ModifierStyleAction;
  existingStyles: KdsModifierStyle[];
  style?: KdsModifierStyle;
  onClose?: () => void;
  onDelete?: () => void;
  onSave: (style: KdsModifierStyle) => void;
}

const MAX_CONDITIONS = MAX_CONDITIONS_PER_STYLE;

const emptyDraft = (nextPriority: number): KdsModifierStyle => ({
  id: uuidv4(),
  name: '',
  styles: {},
  isActive: true,
  priority: nextPriority,
  matchAll: false,
  conditions: [],
});

const SetModifierStyleModal: React.FC<SetModifierStyleModalProps> = ({
  visible,
  action,
  existingStyles,
  style: existingStyle,
  onClose = () => undefined,
  onDelete = () => undefined,
  onSave,
}) => {
  const { translate } = useTranslation();
  const { colors } = useColors();
  const styles = SetModifierStyleModalStyles();

  const [draft, setDraft] = useState<KdsModifierStyle>(
    () =>
      existingStyle ??
      emptyDraft(Math.max(0, ...existingStyles.map(s => s.priority)) + 1),
  );
  const [keywordText, setKeywordText] = useState('');

  const validationError = validateModifierStyle(
    draft,
    existingStyles,
    action === ModifierStyleAction.CREATE,
  );

  // Resolve the structured validation error into a localized message. The
  // match-type (when present) is translated into its own label first.
  const validationMessage = validationError
    ? translate(`kitchenDisplay.${validationError.key}`, {
        ...validationError.params,
        ...(validationError.matchType
          ? {
              matchType: translate(
                validationError.matchType === ModifierKeywordMatchMode.EXACT
                  ? 'kitchenDisplay.modifierStyleMatchExact'
                  : 'kitchenDisplay.modifierStyleMatchContains',
              ),
            }
          : {}),
      })
    : '';

  const onChangeName = (name: string) => {
    setDraft(prev => ({ ...prev, name: name.slice(0, MAX_NAME_LENGTH) }));
  };

  const onSelectMatchAll = () => {
    setDraft(prev => ({ ...prev, matchAll: true }));
  };

  const onSelectConfigureModifiers = () => {
    setDraft(prev => ({ ...prev, matchAll: false }));
  };

  const onSelectColour = (color: string) => {
    setDraft(prev => ({ ...prev, styles: { ...prev.styles, color } }));
  };

  const isNoColourSelected = !draft.styles?.color && !!draft.styles?.italic;

  const onSelectNoColour = () => {
    if (!draft.styles?.italic) return;
    setDraft(prev => ({
      ...prev,
      styles: {
        ...prev.styles,
        color: undefined,
        ...(prev.styles?.background ? { background: false } : {}),
      },
    }));
  };

  const onToggleItalic = () => {
    setDraft(prev => ({
      ...prev,
      styles: { ...prev.styles, italic: !prev.styles?.italic },
    }));
  };

  const onToggleBackground = () => {
    if (!draft.styles?.color) return;
    setDraft(prev => ({
      ...prev,
      styles: { ...prev.styles, background: !prev.styles?.background },
    }));
  };

  const onAddKeyword = () => {
    const keyword = keywordText.trim();
    if (!keyword || draft.conditions.length >= MAX_CONDITIONS) return;
    setDraft(prev => ({
      ...prev,
      conditions: [
        ...prev.conditions,
        { keyword, matchType: ModifierKeywordMatchMode.CONTAINS },
      ],
    }));
    setKeywordText('');
  };

  const onChangeKeywordMatchType = (
    index: number,
    matchType: ModifierKeywordMatchMode,
  ) => {
    setDraft(prev => ({
      ...prev,
      conditions: prev.conditions.map((condition, i) =>
        i === index ? { ...condition, matchType } : condition,
      ),
    }));
  };

  const onRemoveKeyword = (index: number) => {
    setDraft(prev => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== index),
    }));
  };

  const onPressSave = useCallback(() => {
    if (validationError) return;
    onSave(draft);
  }, [draft, validationError, onSave]);

  const onPressDelete = useCallback(() => {
    onDelete();
  }, [onDelete]);

  return (
    <Modal
      animationType="fade"
      onDismiss={onClose}
      onRequestClose={onClose}
      presentationStyle="overFullScreen"
      transparent={true}
      visible={visible}
      supportedOrientations={['landscape', 'landscape-left', 'landscape-right']}
    >
      <View style={styles.background}>
        <View style={styles.panel} testID="modifier-style-modal">
          <View style={styles.header}>
            <Text style={styles.title}>
              {translate(
                action === ModifierStyleAction.CREATE
                  ? 'kitchenDisplay.createModifierStyle'
                  : 'kitchenDisplay.editModifierStyle',
              )}
            </Text>
            <View style={styles.headerActions}>
              {action === ModifierStyleAction.EDIT && (
                <TouchableOpacity
                  style={styles.headerIconButton}
                  testID="btn-delete"
                  onPress={onPressDelete}
                >
                  <Icon name="trash" size={18} color={colors.text} />
                </TouchableOpacity>
              )}
              <TouchableOpacity
                style={styles.headerIconButton}
                testID="btn-dismiss"
                onPress={onClose}
              >
                <Icon name="times" size={20} color={colors.text} />
              </TouchableOpacity>
            </View>
          </View>

          <ScrollView
            style={styles.scrollArea}
            contentContainerStyle={styles.scrollContent}
          >
            <View>
              <View style={styles.fieldLabelRow}>
                <Text style={styles.fieldLabel}>
                  {translate('kitchenDisplay.modifierStyleName')}
                </Text>
                <Text style={styles.counterText}>
                  {draft.name.length}/{MAX_NAME_LENGTH}
                </Text>
              </View>
              <InputText
                onChangeText={onChangeName}
                value={draft.name}
                containerStyle={styles.inputContainer}
                testID="input-modifier-style-name"
                placeholder={translate(
                  'kitchenDisplay.modifierStyleNamePlaceholder',
                )}
              />
            </View>

            <View>
              <Text style={[styles.fieldLabel, styles.sectionLabelSpacing]}>
                {translate('kitchenDisplay.modifierStyleApplyTo')}
              </Text>
              <View
                style={styles.modeToggleGroup}
                accessibilityRole="radiogroup"
              >
                <TouchableOpacity
                  testID="toggle-mode-all"
                  accessibilityRole="radio"
                  accessibilityState={{ checked: !!draft.matchAll }}
                  style={[
                    styles.modeToggleOption,
                    !!draft.matchAll && styles.modeToggleOptionSelected,
                  ]}
                  onPress={onSelectMatchAll}
                >
                  <Text
                    style={[
                      styles.modeToggleLabel,
                      !!draft.matchAll && styles.modeToggleLabelSelected,
                    ]}
                  >
                    {translate('kitchenDisplay.modifierStyleModeAllModifiers')}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  testID="toggle-mode-configure"
                  accessibilityRole="radio"
                  accessibilityState={{ checked: !draft.matchAll }}
                  style={[
                    styles.modeToggleOption,
                    !draft.matchAll && styles.modeToggleOptionSelected,
                  ]}
                  onPress={onSelectConfigureModifiers}
                >
                  <Text
                    style={[
                      styles.modeToggleLabel,
                      !draft.matchAll && styles.modeToggleLabelSelected,
                    ]}
                  >
                    {translate(
                      'kitchenDisplay.modifierStyleModeConfigureModifiers',
                    )}
                  </Text>
                </TouchableOpacity>
              </View>
            </View>

            <View>
              <Text style={[styles.fieldLabel, styles.sectionLabelSpacing]}>
                {translate('kitchenDisplay.modifierStyleColor')}
              </Text>
              <View style={styles.swatchGrid}>
                {theme.colors.pools.modifiers.map(swatch => {
                  const selected = draft.styles?.color === swatch.color;
                  return (
                    <TouchableOpacity
                      key={swatch.color}
                      testID={`swatch-${swatch.color}`}
                      style={[styles.swatch, selected && styles.swatchSelected]}
                      onPress={() => onSelectColour(swatch.color)}
                    >
                      <View
                        style={[
                          styles.swatchDot,
                          { backgroundColor: swatch.color },
                        ]}
                      />
                      <Text style={styles.swatchLabel} numberOfLines={2}>
                        {swatch.name}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
              <TouchableOpacity
                testID="swatch-no-colour"
                disabled={!draft.styles?.italic}
                style={[
                  styles.noColourRow,
                  isNoColourSelected && styles.swatchSelected,
                  !draft.styles?.italic && styles.noColourRowDisabled,
                ]}
                onPress={onSelectNoColour}
              >
                <View style={styles.noColourDot}>
                  <View style={styles.noColourSlash} />
                </View>
                <View style={styles.noColourTextGroup}>
                  <Text style={styles.noColourLabel}>
                    {translate('kitchenDisplay.modifierStyleNoColor')}
                  </Text>
                  <Text style={styles.noColourHelper}>
                    {translate('kitchenDisplay.modifierStyleNoColorHelper')}
                  </Text>
                </View>
              </TouchableOpacity>
            </View>

            <View>
              <Text style={[styles.fieldLabel, styles.sectionLabelSpacing]}>
                {translate('kitchenDisplay.modifierStyleTextStyle')}
              </Text>
              <View style={styles.italicToggleRow}>
                <TouchableOpacity
                  testID="toggle-modifier-style-italic"
                  style={[
                    styles.italicToggle,
                    draft.styles?.italic && styles.italicToggleSelected,
                  ]}
                  onPress={onToggleItalic}
                >
                  <Icon name="italic" size={14} color={colors.text} />
                  <Text style={styles.italicToggleLabel}>
                    {translate('kitchenDisplay.modifierStyleItalic')}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  testID="toggle-modifier-style-background"
                  disabled={!draft.styles?.color}
                  style={[
                    styles.italicToggle,
                    draft.styles?.background && styles.italicToggleSelected,
                    !draft.styles?.color && styles.noColourRowDisabled,
                  ]}
                  onPress={onToggleBackground}
                >
                  <Icon name="paint-tool" size={14} color={colors.text} />
                  <Text style={styles.italicToggleLabel}>
                    {translate('kitchenDisplay.modifierStyleBackground')}
                  </Text>
                </TouchableOpacity>
              </View>
            </View>

            {!draft.matchAll && (
              <View>
                <View style={styles.fieldLabelRow}>
                  <Text style={styles.fieldLabel}>
                    {translate('kitchenDisplay.modifierStyleKeywords')}
                  </Text>
                  <Text style={styles.counterText}>
                    {draft.conditions.length}/{MAX_CONDITIONS}
                  </Text>
                </View>
                <View style={styles.row}>
                  <InputText
                    onChangeText={setKeywordText}
                    value={keywordText}
                    onSubmitEditing={onAddKeyword}
                    containerStyle={[
                      styles.inputContainer,
                      styles.keywordInput,
                    ]}
                    testID="input-modifier-style-keyword"
                    placeholder={translate(
                      'kitchenDisplay.modifierStyleKeywordPlaceholder',
                    )}
                  />
                  <Button
                    testID="btn-add-keyword"
                    label={translate('kitchenDisplay.modifierStyleAddKeyword')}
                    type="active"
                    onPress={onAddKeyword}
                    containerStyle={styles.keywordAddButton}
                  />
                </View>
                <Text style={styles.helperText}>
                  {translate('kitchenDisplay.modifierStyleKeywordsHelper')}
                </Text>
                {draft.conditions.length > 0 && (
                  <View style={styles.keywordList}>
                    {draft.conditions.map((condition, index) => {
                      const isExact =
                        condition.matchType === ModifierKeywordMatchMode.EXACT;
                      return (
                        <View key={index} style={styles.keywordChip}>
                          <Text
                            numberOfLines={1}
                            style={styles.keywordChipText}
                          >
                            <Text
                              style={[
                                draft.styles?.background &&
                                  styles.keywordChipTextBackgroundPill,
                                modifierTextColorStyle(draft.styles?.color),
                                draft.styles?.background &&
                                  modifierBackgroundColorStyle(
                                    draft.styles?.color,
                                  ),
                                draft.styles?.italic
                                  ? styles.keywordChipTextItalic
                                  : null,
                              ]}
                            >
                              {condition.keyword}
                            </Text>
                          </Text>
                          <View style={styles.keywordChipRight}>
                            <View style={styles.matchTypeGroup}>
                              <TouchableOpacity
                                testID={`toggle-match-exact-${index}`}
                                style={[
                                  styles.matchTypeOption,
                                  isExact && styles.matchTypeOptionSelected,
                                ]}
                                onPress={() =>
                                  onChangeKeywordMatchType(
                                    index,
                                    ModifierKeywordMatchMode.EXACT,
                                  )
                                }
                              >
                                <Text
                                  style={[
                                    styles.matchTypeLabel,
                                    isExact && styles.matchTypeLabelSelected,
                                  ]}
                                >
                                  {translate(
                                    'kitchenDisplay.modifierStyleMatchExact',
                                  )}
                                </Text>
                              </TouchableOpacity>
                              <TouchableOpacity
                                testID={`toggle-match-contains-${index}`}
                                style={[
                                  styles.matchTypeOption,
                                  !isExact && styles.matchTypeOptionSelected,
                                ]}
                                onPress={() =>
                                  onChangeKeywordMatchType(
                                    index,
                                    ModifierKeywordMatchMode.CONTAINS,
                                  )
                                }
                              >
                                <Text
                                  style={[
                                    styles.matchTypeLabel,
                                    !isExact && styles.matchTypeLabelSelected,
                                  ]}
                                >
                                  {translate(
                                    'kitchenDisplay.modifierStyleMatchContains',
                                  )}
                                </Text>
                              </TouchableOpacity>
                            </View>
                            <TouchableOpacity
                              style={styles.keywordRemove}
                              testID={`btn-remove-keyword-${index}`}
                              onPress={() => onRemoveKeyword(index)}
                            >
                              <Icon
                                name="times"
                                size={16}
                                color={colors.textLight}
                              />
                            </TouchableOpacity>
                          </View>
                        </View>
                      );
                    })}
                  </View>
                )}
              </View>
            )}
            {!!validationError && (
              <Text style={styles.errorText} testID="modifier-style-error">
                {validationMessage}
              </Text>
            )}
          </ScrollView>

          <View style={styles.actions}>
            <Button
              testID="btn-save"
              label={translate('kitchenDisplay.modifierStyleSave')}
              type={validationError ? 'interface' : 'positive'}
              onPress={onPressSave}
              disabled={!!validationError}
              containerStyle={styles.saveButton}
            />
            <Button
              testID="btn-cancel"
              label={translate('button.cancel')}
              type="active"
              onPress={onClose}
              containerStyle={styles.cancelButton}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
};

export default SetModifierStyleModal;
